import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Avatar from '../../../../../../atomic/molecules/avatar/Avatar';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { useProjectMatchingEmployeesQuery } from '../../../../__generated__/graphql';
import { getEmployeeInitialsFromName } from '../../../../utils/employeeInitials';
import { managerRoutes } from '../../../../manager/routes';
import { SkillLevelLabel } from '../../../Skills/common/SkillLevelLabel';
import { isSkillLevel, SkillLevel } from '../../../Skills/common/types';

const MAX_VISIBLE_MATCHES = 5;

type Props = {
  projectId: string;
};

type SharedSkill = { Id: string; name: string; rate: number | null };

const toSkillLevel = (rate: number | null | undefined): SkillLevel =>
  isSkillLevel(rate) ? rate : null;

export const ProjectMatchingEmployeesSection: React.FC<
  React.PropsWithChildren<Props>
> = ({ projectId }) => {
  const t = useNamespacedTranslation('skills.projectMatchingEmployees');
  const { data, loading } = useProjectMatchingEmployeesQuery({
    variables: { projectId },
    // Editing a required skill refetches this query (see useProjectSkillsMutations).
    // Without this, `loading` only flips on the initial fetch and the header
    // spinner would never appear for the refetch it exists to signal.
    notifyOnNetworkStatusChange: true,
  });

  if (loading && !data) {
    return (
      <Card>
        <Card.Header>
          <div className="fw-bold">{t('header')}</div>
        </Card.Header>
        <Card.Body>
          <Skeleton count={3} height={40} className="mb-2" />
        </Card.Body>
      </Card>
    );
  }

  const matches = data?.project?.matchingEmployees ?? [];
  const visible = matches.slice(0, MAX_VISIBLE_MATCHES);
  const hiddenCount = matches.length - visible.length;

  return (
    <Card>
      <Card.Header>
        <div className="d-flex align-items-center gap-2">
          <div className="fw-bold">{t('header')}</div>
          {/* Refetch (e.g. after editing required skills) keeps the current list
              on screen rather than collapsing it into a skeleton — the spinner
              is the only signal that fresher matches are on the way. */}
          {loading && (
            <Spinner
              animation="border"
              size="sm"
              className="text-muted"
              role="status"
              aria-label={t('refreshing')}
            />
          )}
        </div>
      </Card.Header>
      <Card.Body className="d-flex flex-column gap-3">
        {matches.length === 0 && (
          <div className="text-muted small">{t('empty')}</div>
        )}
        {visible.map((employee) => {
          const badge = (
            <Badge variant="primary" pill>
              {t('matchPercentage', {
                value: Math.round(employee.matchPercentage),
              })}
            </Badge>
          );
          const content = (
            <>
              <Avatar
                size="sm"
                initials={getEmployeeInitialsFromName(employee.name)}
              />
              <div className="flex-grow-1 fw-semibold">{employee.name}</div>
              {employee.sharedSkills.length > 0 ? (
                <OverlayTrigger
                  placement="left"
                  overlay={renderSkillsPopover(
                    employee.Id,
                    employee.sharedSkills,
                    t,
                  )}>
                  {badge}
                </OverlayTrigger>
              ) : (
                badge
              )}
            </>
          );

          // A match the current user cannot open (visible in their scope, but no
          // manager access) is shown for context but is not a link — the profile
          // route would just redirect them away.
          return employee.canOpen ? (
            <Link
              key={employee.Id}
              to={managerRoutes.employeePage.route.create({
                employeeId: employee.Id,
              })}
              className="d-flex align-items-center gap-3 text-decoration-none text-reset">
              {content}
            </Link>
          ) : (
            <div
              key={employee.Id}
              className="d-flex align-items-center gap-3 text-muted"
              title={t('noAccess')}>
              {content}
            </div>
          );
        })}
        {hiddenCount > 0 && (
          <div className="text-muted small">
            {t('moreMatches', { count: hiddenCount })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const renderSkillsPopover = (
  employeeId: string,
  sharedSkills: readonly SharedSkill[],
  t: (key: string, options?: Record<string, unknown>) => string,
) => (
  <Popover id={`matching-skills-${employeeId}`}>
    <Popover.Header>{t('skillsHeader')}</Popover.Header>
    <Popover.Body className="d-flex flex-column gap-2">
      {sharedSkills.map((skill) => {
        const employeeLevel = toSkillLevel(skill.rate);
        return (
          <div
            key={skill.Id}
            className="d-flex align-items-center justify-content-between gap-3">
            <span className="small">{skill.name}</span>
            {skill.rate != null && (
              <SkillLevelLabel
                value={employeeLevel}
                title={t('employeeLevel')}
              />
            )}
          </div>
        );
      })}
    </Popover.Body>
  </Popover>
);
